import React from "react";
import { Button, Progress, Chip, Tooltip } from "@heroui/react";
import { FaBuilding, FaMapMarkerAlt, FaEye } from "react-icons/fa";
import { FaTriangleExclamation } from "react-icons/fa6";
import { usePlans } from "@/hooks/usePlans";
import { useNavigate } from "react-router-dom";

interface PlanLimitsCompactProps {
  currentOffices?: number;
  currentLocations?: number;
  className?: string;
}

export const PlanLimitsCompact: React.FC<PlanLimitsCompactProps> = ({
  currentOffices = 0,
  currentLocations = 0,
  className = "",
}) => {
  const { currentPlan, getPlanLimits } = usePlans();
  const navigate = useNavigate();

  if (!currentPlan) {
    return null;
  }

  const limits = getPlanLimits(currentPlan);

  const getUsagePercentage = (current: number, limit: number) => {
    if (limit === -1) return 0;
    if (limit === 0) return 100;
    return Math.min((current / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return "danger";
    if (percentage >= 75) return "warning";
    return "success";
  };

  const officeUsage = getUsagePercentage(currentOffices, limits.offices);
  const locationUsage = getUsagePercentage(currentLocations, limits.locations);

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <Chip color="primary" size="sm" variant="flat">
        {currentPlan.display_name}
      </Chip>

      <div className="flex items-center gap-2">
        <FaBuilding className="text-primary text-sm" />
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Офисы:</span>
          <span className="text-sm text-foreground-500">
            {currentOffices} /{" "}
            {limits.hasUnlimitedOffices ? "∞" : limits.offices}
          </span>
          {!limits.hasUnlimitedOffices && (
            <div className="w-16">
              <Progress
                value={officeUsage}
                color={getUsageColor(officeUsage)}
                size="sm"
                className="min-w-0"
              />
            </div>
          )}
          {officeUsage >= 90 && !limits.hasUnlimitedOffices && (
            <Tooltip content="Приближается к лимиту">
              <FaTriangleExclamation className="text-warning text-xs" />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <FaMapMarkerAlt className="text-primary text-sm" />
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Локации:</span>
          <span className="text-sm text-foreground-500">
            {currentLocations} /{" "}
            {limits.hasUnlimitedLocations ? "∞" : limits.locations}
          </span>
          {!limits.hasUnlimitedLocations && (
            <div className="w-16">
              <Progress
                value={locationUsage}
                color={getUsageColor(locationUsage)}
                size="sm"
                className="min-w-0"
              />
            </div>
          )}
          {locationUsage >= 90 && !limits.hasUnlimitedLocations && (
            <Tooltip content="Приближается к лимиту">
              <FaTriangleExclamation className="text-warning text-xs" />
            </Tooltip>
          )}
        </div>
      </div>

      <Button
        size="sm"
        variant="light"
        startContent={<FaEye />}
        className="text-primary"
        onPress={() => navigate("/dashboard/plans")}
      >
        Просмотреть
      </Button>
    </div>
  );
};
